import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from '../../utils/baseQuery'

export interface Application {
  _id: string;
  status: 'applied' | 'scheduled' | 'selected' | 'under-review' | 'submitted' | 'rejected';
  name: string;
  email: string;
  phone: string;
  profileImage: string;
  submittedDate: string;
}

export interface ApplicationWorkInfo {
  employeeId?: string;
  projectName?: string;
  branch?: string;
  shift?: string;
  reference?: string;
}

export interface ApplicationWorkInfoResponse {
  success: boolean;
  message: string;
  data: {
    profile?: {
      workInfo?: ApplicationWorkInfo | null;
    };
  };
}

export interface PaginationLinks {
  self: string;
  next: string | null;
  prev: string | null;
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  links: PaginationLinks;
}

export interface ApplicationsResponse {
  success: boolean;
  message: string;
  data: {
    applications: Application[];
    meta: PaginationMeta;
  };
}

export const applicationApi = createApi({
  reducerPath: 'applicationApi',
  baseQuery,
  endpoints: (builder) => ({
    getApplications: builder.query<
      ApplicationsResponse,
      { page?: number; limit?: number; search?: string; status?: string; appliedFrom?: string; appliedTo?: string }
    >({
      query: ({ page = 1, limit = 10, search = '', status = '', appliedFrom = '', appliedTo = '' }) => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          ...(appliedFrom ? { appliedFrom } : {}),
          ...(appliedTo ? { appliedTo } : {}),
        });

        return `/applications?${params.toString()}`;
      },
    }),
    getApplicationById: builder.query<any, string>({
      query: (id) => `/applications/${id}`,
    }),
    getApplicationWorkInfoById: builder.query<ApplicationWorkInfo | null, string>({
      query: (id) => `/applications/${id}`,
      transformResponse: (response: ApplicationWorkInfoResponse) => response?.data?.profile?.workInfo || null,
    }),
    updateApplication: builder.mutation<Application, { id: string; status: string; adminNotes?: string; rejectionReason?: string, remarkText?: string }>({
      query: ({ id, ...body }) => ({
        url: `/applications/${id}`,
        method: 'PUT',
        body,
      }),
    }),
    updateApplicationWorkInfo: builder.mutation<any, { id: string; employeeId: string; projectName: string; branch: string; shift: string; reference?: string }>({
      query: ({ id, ...body }) => ({
        url: `/applications/${id}/work-info`,
        method: 'PUT',
        body,
      }),
    }),
    updateApplicationPermissions: builder.mutation<any, { id: string; canEditFirstPhase?: boolean; canEditSecondPhase?: boolean }>({
      query: ({ id, ...body }) => ({
        url: `/applications/${id}/permissions`,
        method: 'PUT',
        body,
      }),
    }),
  }),
});


export const {
  useGetApplicationsQuery,
  useGetApplicationByIdQuery,
  useLazyGetApplicationWorkInfoByIdQuery,
  useUpdateApplicationMutation,
  useUpdateApplicationWorkInfoMutation,
  useUpdateApplicationPermissionsMutation,
} = applicationApi
